import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import {
  Disclose,
  EmptyState,
  Loading,
  Permission,
  Refusal,
  hostConfig,
  kernel,
} from "@lopie/kernel-ui";

/**
 * Service Desk — one place for the field service team's tickets.
 *
 * Tickets live in this app's own store (the kernel has no ticket type).
 * Customers come from the kernel, under this app's reviewed manifest: the
 * types are read from this app's row in GET /apps and their collection paths
 * and label fields from GET /schema, so nothing about the kernel's shape is
 * written down here. A ticket stores only a reference (`Type:id`) to its
 * customer, never a copy of the name, so a person who may not see that
 * customer in the kernel is told so rather than shown it.
 */

const PAGE = 200;
const MAX_PAGES = 5;

function labelOf(list, key) {
  return (list || []).find((x) => x.key === key)?.label || key || "—";
}

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function OwnStoreProblem({ problem, onRetry }) {
  return (
    <Alert
      severity="warning"
      variant="outlined"
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Try again
          </Button>
        ) : null
      }
    >
      <AlertTitle>
        The ticket store did not answer{problem?.status ? ` (${problem.status})` : ""}
      </AlertTitle>
      {problem?.body?.detail || problem?.body?.error || problem?.message ||
        "No reply."}{" "}
      This is the app&apos;s own ticket list, not the kernel: customer records are
      unaffected, and no tickets are shown because none could be read, not
      because there are none.
    </Alert>
  );
}

export default function App({ token, claims, onSignOut }) {
  const get = useMemo(() => kernel(token), [token]);
  const ownApi = hostConfig()?.appServiceDeskApi || "";
  const me = claims?.username || claims?.email || "";

  const [kernelFailure, setKernelFailure] = useState(null);
  const [customerTypes, setCustomerTypes] = useState(null);
  const [effective, setEffective] = useState({ permitted: [], declared: null });

  const [tickets, setTickets] = useState(null);
  const [vocab, setVocab] = useState({ statuses: [], priorities: [], topics: [] });
  const [storeProblem, setStoreProblem] = useState(null);

  const [view, setView] = useState("open");
  const [topic, setTopic] = useState("");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);

  const own = useCallback(
    async (path, init) => {
      const r = await fetch(ownApi + path, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "application/json",
          ...(init?.headers || {}),
        },
      });
      const text = await r.text();
      let body = null;
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
      if (!r.ok) {
        throw Object.assign(new Error(`HTTP ${r.status}`), { status: r.status, body });
      }
      return body;
    },
    [ownApi, token],
  );

  const loadCustomers = useCallback(async () => {
    try {
      const [apps, schema] = await Promise.all([get("/apps"), get("/schema")]);
      const self = (apps.apps || []).find((a) => a.is_self);
      const declared = (self?.reads || []).map((g) => g.type);
      const described = schema.types || [];
      const byType = Object.fromEntries(described.map((t) => [t.type, t]));
      const wanted = declared.filter((t) => byType[t]?.collection);

      const answered = await Promise.all(
        wanted.map(async (type) => {
          const info = byType[type];
          try {
            let items = [];
            let cursor;
            let body = null;
            let more = false;
            for (let i = 0; i < MAX_PAGES; i += 1) {
              const params = cursor ? { limit: PAGE, cursor } : { limit: PAGE };
              body = await get(`/${info.collection}`, params);
              items = items.concat(body.items || []);
              more = !!body.has_more;
              if (!more || !body.cursor) break;
              cursor = body.cursor;
            }
            return {
              type,
              label: info.label || "",
              items,
              truncated: more,
              permitted_tags: body?.permitted_tags || [],
              declared_tags: body?.declared_tags || null,
              refusal: null,
            };
          } catch (e) {
            return { type, label: info.label || "", items: [], refusal: e };
          }
        }),
      );
      setCustomerTypes(answered);
      const firstOk = answered.find((p) => !p.refusal);
      setEffective({
        permitted: firstOk?.permitted_tags || [],
        declared: firstOk?.declared_tags || null,
      });
      setKernelFailure(null);
    } catch (e) {
      setKernelFailure(e);
      setCustomerTypes([]);
    }
  }, [get]);

  const loadTickets = useCallback(async () => {
    try {
      const body = await own("/tickets");
      setTickets(body?.items || []);
      if (body?.vocabulary) setVocab(body.vocabulary);
      setStoreProblem(null);
    } catch (e) {
      setTickets(null);
      setStoreProblem(e);
    }
  }, [own]);

  useEffect(() => {
    loadCustomers();
    loadTickets();
  }, [loadCustomers, loadTickets]);

  // Every customer this person may see, keyed by the reference a ticket stores.
  const customers = useMemo(() => {
    const out = {};
    (customerTypes || []).forEach((p) => {
      (p.items || []).forEach((item) => {
        out[`${p.type}:${item.id}`] = {
          ref: `${p.type}:${item.id}`,
          type: p.type,
          name: (p.label && item.fields?.[p.label]) || item.id,
        };
      });
    });
    return out;
  }, [customerTypes]);

  const customerName = useCallback(
    (ref) => {
      if (!ref) return "No customer linked";
      if (customers[ref]) return customers[ref].name;
      const type = ref.split(":")[0];
      return `A ${type} record you cannot see`;
    },
    [customers],
  );

  const doneKey = "done";
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (tickets || [])
      .filter((t) => {
        if (view === "open") return t.status !== doneKey;
        if (view === "mine") return t.status !== doneKey && t.assignee === me;
        if (view === "done") return t.status === doneKey;
        return true;
      })
      .filter((t) => !topic || t.topic === topic)
      .filter((t) => {
        if (!q) return true;
        const hay = [t.id, t.title, t.site, t.assignee, t.description, customerName(t.customer_ref)]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const pa = (vocab.priorities || []).findIndex((p) => p.key === a.priority);
        const pb = (vocab.priorities || []).findIndex((p) => p.key === b.priority);
        if (pa !== pb) return pb - pa;
        return String(b.updated_at || "").localeCompare(String(a.updated_at || ""));
      });
  }, [tickets, view, topic, search, me, customerName, vocab.priorities]);

  const openCount = (tickets || []).filter((t) => t.status !== doneKey).length;
  const byStatus = (vocab.statuses || []).map((s) => ({
    ...s,
    count: (tickets || []).filter((t) => t.status === s.key).length,
  }));
  const current = (tickets || []).find((t) => t.id === openId) || null;

  if (customerTypes === null && tickets === null && !storeProblem) {
    return <Loading label="Reading tickets and customers" minHeight="60vh" />;
  }

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, maxWidth: 1200, mx: "auto" }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}
      >
        <Typography variant="h1">Service Desk</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {me}
          </Typography>
          <Typography
            component="button"
            variant="body2"
            onClick={onSignOut}
            sx={{ background: "none", border: 0, cursor: "pointer", color: "primary.main" }}
          >
            Sign out
          </Typography>
        </Stack>
      </Stack>

      <Permission
        gate={
          "Tickets are this app's own records and everyone who may open the app sees them. "
          + "Customer names come from the kernel under this app's reviewed manifest "
          + "(customer Organizations and People), met with your own groups; a ticket "
          + "whose customer you may not see says so instead of naming it."
        }
        groups={claims?.["cognito:groups"] || []}
        effective={effective}
      />

      {kernelFailure ? (
        <Refusal failure={kernelFailure} actor="Service Desk" onRetry={loadCustomers} />
      ) : null}
      {(customerTypes || [])
        .filter((p) => p.refusal)
        .map((p) => (
          <Box key={p.type}>
            <Typography variant="overline" sx={{ display: "block" }}>
              Customers · {p.type}
            </Typography>
            <Refusal failure={p.refusal} actor="Service Desk" onRetry={loadCustomers} />
          </Box>
        ))}

      {storeProblem ? (
        <OwnStoreProblem problem={storeProblem} onRetry={loadTickets} />
      ) : (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ alignItems: { xs: "flex-start", sm: "flex-end" }, justifyContent: "space-between" }}
          >
            <Box
              sx={{ borderLeft: 2, borderColor: "primary.main", pl: 2 }}
              title="Counted from this app's ticket list as it is now: every ticket whose status is not Done."
            >
              <Typography sx={{ fontSize: "3rem", fontWeight: 300, lineHeight: 1.1 }}>
                {openCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                open tickets · now
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: 1 }}>
              {byStatus.map((s) => (
                <Box key={s.key} title={`Tickets whose status is ${s.label}, now`}>
                  <Typography sx={{ fontSize: "1.25rem", fontWeight: 300 }}>{s.count}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Button variant="contained" onClick={() => setCreating(true)}>
              New ticket
            </Button>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ alignItems: { md: "center" } }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, v) => v && setView(v)}
              aria-label="Which tickets"
              sx={{ flexWrap: "wrap" }}
            >
              <ToggleButton value="open">All open</ToggleButton>
              <ToggleButton value="mine">Assigned to me</ToggleButton>
              <ToggleButton value="done">Done</ToggleButton>
              <ToggleButton value="all">Everything</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              select
              size="small"
              label="Topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Any topic</MenuItem>
              {(vocab.topics || []).map((t) => (
                <MenuItem key={t.key} value={t.key}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Search"
              placeholder="Title, customer, site, person…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Stack>

          {tickets && tickets.length === 0 ? (
            <EmptyState title="No tickets yet">
              <Typography variant="body2">
                The ticket list answered and is empty: nobody has opened a ticket.
                Use New ticket to log the first one.
              </Typography>
            </EmptyState>
          ) : visible.length === 0 ? (
            <EmptyState title="No tickets match these filters">
              <Typography variant="body2">
                There are {tickets?.length || 0} tickets in all; none match the view,
                topic and search chosen above.
              </Typography>
            </EmptyState>
          ) : (
            <Stack spacing={1} component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
              {visible.map((t) => (
                <Paper
                  key={t.id}
                  component="li"
                  variant="outlined"
                  sx={{ p: 2, minWidth: 0 }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ justifyContent: "space-between", alignItems: { sm: "flex-start" } }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        component="button"
                        onClick={() => setOpenId(t.id)}
                        sx={{
                          background: "none",
                          border: 0,
                          p: 0,
                          cursor: "pointer",
                          textAlign: "left",
                          color: "text.primary",
                          fontWeight: 600,
                          fontSize: "1rem",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {t.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                        {t.id} · {customerName(t.customer_ref)}
                        {t.site ? ` · ${t.site}` : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.assignee ? `Assigned to ${t.assignee}` : "Unassigned"}
                        {t.due_date ? ` · Due ${t.due_date}` : ""}
                        {` · Updated ${when(t.updated_at)}`}
                        {(t.notes || []).length ? ` · ${(t.notes || []).length} notes` : ""}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1, flexShrink: 0 }}>
                      <Chip size="small" variant="outlined" label={`Status: ${labelOf(vocab.statuses, t.status)}`} />
                      <Chip size="small" variant="outlined" label={`Priority: ${labelOf(vocab.priorities, t.priority)}`} />
                      <Chip size="small" variant="outlined" label={labelOf(vocab.topics, t.topic)} />
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </>
      )}

      <Disclose label="Where this information comes from">
        <Stack spacing={1.5} sx={{ maxWidth: "62ch" }}>
          <Typography variant="body2" color="text.secondary">
            Tickets are kept in this app&apos;s own store, because the kernel has no
            ticket type. Everyone who may open this app sees every ticket.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customers are read live from the kernel: the types this app declares,
            limited to the customer tag, and then to what your own groups allow.
            A ticket keeps only a reference to its customer, so if you cannot see
            that customer the ticket says so rather than naming them.
          </Typography>
          {(customerTypes || []).some((p) => p.truncated) ? (
            <Typography variant="body2" color="text.secondary">
              More customers exist than this screen reads at once; the customer
              picker shows the first {PAGE * MAX_PAGES} of each type.
            </Typography>
          ) : null}
        </Stack>
      </Disclose>

      {creating ? (
        <NewTicket
          vocab={vocab}
          customers={customers}
          customerTypes={customerTypes || []}
          me={me}
          own={own}
          onClose={() => setCreating(false)}
          onCreated={(t) => {
            setCreating(false);
            loadTickets();
            setOpenId(t?.id || null);
          }}
        />
      ) : null}

      {current ? (
        <TicketDetail
          key={current.id}
          ticket={current}
          vocab={vocab}
          me={me}
          own={own}
          customerName={customerName}
          onClose={() => setOpenId(null)}
          onChanged={loadTickets}
        />
      ) : null}
    </Stack>
  );
}

function NewTicket({ vocab, customers, customerTypes, me, own, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    topic: vocab.topics?.[0]?.key || "",
    priority: "normal",
    customer_ref: "",
    site: "",
    assignee: me,
    due_date: "",
    description: "",
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const options = Object.values(customers).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const anyRefused = customerTypes.some((p) => p.refusal);

  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const t = await own("/tickets", { method: "POST", body: JSON.stringify(form) });
      onCreated(t?.ticket);
    } catch (e) {
      setProblem(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New ticket</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="What needs doing" required value={form.title} onChange={set("title")} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField select label="Topic" value={form.topic} onChange={set("topic")} sx={{ flex: 1 }}>
              {(vocab.topics || []).map((t) => (
                <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Priority" value={form.priority} onChange={set("priority")} sx={{ flex: 1 }}>
              {(vocab.priorities || []).map((p) => (
                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            select
            label="Customer"
            value={form.customer_ref}
            onChange={set("customer_ref")}
            helperText={
              options.length
                ? "Customers you may see in the kernel."
                : anyRefused
                  ? "This app was refused the customer list; see the notice on the main screen."
                  : "You may not see any customer records, so this ticket will have no customer linked."
            }
          >
            <MenuItem value="">No customer</MenuItem>
            {options.map((c) => (
              <MenuItem key={c.ref} value={c.ref}>
                {c.name} ({c.type})
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Site / address" value={form.site} onChange={set("site")} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Assigned to" value={form.assignee} onChange={set("assignee")} sx={{ flex: 1 }} />
            <TextField
              label="Due date"
              type="date"
              value={form.due_date}
              onChange={set("due_date")}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField label="Details" multiline minRows={3} value={form.description} onChange={set("description")} />
          {problem ? (
            <Alert severity="error" variant="outlined">
              <AlertTitle>Not saved{problem.status ? ` (${problem.status})` : ""}</AlertTitle>
              {problem.body?.detail || problem.body?.error || problem.message}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !form.title.trim()}>
          {busy ? "Saving…" : "Create ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TicketDetail({ ticket, vocab, me, own, customerName, onClose, onChanged }) {
  const [form, setForm] = useState({
    status: ticket.status,
    priority: ticket.priority,
    topic: ticket.topic,
    assignee: ticket.assignee || "",
    due_date: ticket.due_date || "",
    site: ticket.site || "",
  });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const run = async (fn, said) => {
    setBusy(true);
    setProblem(null);
    setDone(null);
    try {
      await fn();
      setDone(said);
      await onChanged();
    } catch (e) {
      setProblem(e);
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(
      () => own(`/tickets/${encodeURIComponent(ticket.id)}`, { method: "POST", body: JSON.stringify(form) }),
      "Changes saved.",
    );
  const addNote = () =>
    run(async () => {
      await own(`/tickets/${encodeURIComponent(ticket.id)}/notes`, {
        method: "POST",
        body: JSON.stringify({ text: note }),
      });
      setNote("");
    }, "Note added.");

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ overflowWrap: "anywhere" }}>{ticket.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {ticket.id} · Customer: {customerName(ticket.customer_ref)} · Opened by{" "}
            {ticket.created_by || "unknown"} on {when(ticket.created_at)}
          </Typography>
          {ticket.description ? (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {ticket.description}
            </Typography>
          ) : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField select label="Status" value={form.status} onChange={set("status")} sx={{ flex: 1 }}>
              {(vocab.statuses || []).map((s) => (
                <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Priority" value={form.priority} onChange={set("priority")} sx={{ flex: 1 }}>
              {(vocab.priorities || []).map((p) => (
                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Topic" value={form.topic} onChange={set("topic")} sx={{ flex: 1 }}>
              {(vocab.topics || []).map((t) => (
                <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Assigned to" value={form.assignee} onChange={set("assignee")} sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setForm((f) => ({ ...f, assignee: me }))} disabled={!me}>
              Assign to me
            </Button>
            <TextField
              label="Due date"
              type="date"
              value={form.due_date}
              onChange={set("due_date")}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField label="Site / address" value={form.site} onChange={set("site")} />
          <Box>
            <Button variant="contained" onClick={save} disabled={busy}>
              Save changes
            </Button>
          </Box>

          <Typography variant="overline">Notes · newest first</Typography>
          {(ticket.notes || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No notes on this ticket yet.
            </Typography>
          ) : (
            <Stack spacing={1} component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
              {[...(ticket.notes || [])].reverse().map((n, i) => (
                <Paper component="li" variant="outlined" key={`${n.at}-${i}`} sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {n.by || "unknown"} · {when(n.at)}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {n.text}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}
          <TextField
            label="Add a note"
            multiline
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Box>
            <Button variant="outlined" onClick={addNote} disabled={busy || !note.trim()}>
              Add note
            </Button>
          </Box>
          {done ? (
            <Alert severity="info" variant="outlined">
              {done} Saved to this app&apos;s ticket list only; nothing about the customer
              in the kernel was changed, and nobody was notified.
            </Alert>
          ) : null}
          {problem ? (
            <Alert severity="error" variant="outlined">
              <AlertTitle>Not saved{problem.status ? ` (${problem.status})` : ""}</AlertTitle>
              {problem.body?.detail || problem.body?.error || problem.message}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
